import type { BetaKey, BetaSource } from './constants';

import { toDaffyPath, toFrontendDashboardPath } from './rolloutPaths';

export type NavAction = { type: 'reload' } | { type: 'assign'; url: string };

/**
 * Post-success navigation strategy, driven by which surface toggled and which key
 * (FRONTEND-655 / FRONTEND-656):
 *
 * | Toggle                    | agentMenu / banner                                              | settings           |
 * | ------------------------- | --------------------------------------------------------------- | ------------------ |
 * | Applications (betaRoutes) | registered → registry beta path; else → `/beta/journeys/applications` | reload current URL |
 * | Side nav (fepSidenav)     | reload current URL                                              | reload current URL |
 *
 * Settings never hard-navs to /beta (no /beta Settings page exists), so it always reloads
 * the current legacy URL; the sidenav toggle reloads the current URL on every surface.
 *
 * Disabling Applications from a legacy (non-/beta) page is a no-op reload — the user is
 * already on the correct page, so there's nothing to map back to.
 *
 * Path pairs come from David's `routeRolloutRegistry` via local helpers.
 *
 * TODO(FRONTEND-608): Switch `toDaffyPath` / `toFrontendDashboardPath` imports from
 * `./rolloutPaths` to `@usealloy/daffy-to-frontend-dashboard-rollout` once David's FD
 * counterpart exposes those helpers; then delete the local shim.
 */
export function resolveNavAction({
  source,
  key,
  enabled,
  pathname,
}: {
  source: BetaSource;
  key: BetaKey;
  enabled: boolean;
  pathname: string;
}): NavAction {
  // Settings and the sidenav toggle only ever reload the current URL.
  if (source === 'settings' || key === 'fepSidenav') {
    return { type: 'reload' };
  }
  // Applications toggle from a menu/banner crosses between legacy and /beta.
  if (enabled) {
    // Registered pages map 1:1. Unregistered pages land on Applications — do not
    // `/beta`-prefix the current URL (FE2.0's v3-strip + catch-all 404s those, and
    // BetaGate never gets a chance to bounce).
    return {
      type: 'assign',
      url: toFrontendDashboardPath(pathname) ?? '/beta/journeys/applications',
    };
  }
  // Leaving beta from a legacy (non-/beta) page is a no-op — there's nowhere to navigate to.
  if (!pathname.startsWith('/beta')) {
    return { type: 'reload' };
  }
  return { type: 'assign', url: toDaffyPath(pathname) };
}

/** Executes a resolved nav action against the real browser location. */
export function applyNavAction(action: NavAction): void {
  if (action.type === 'reload') {
    window.location.reload();
    return;
  }
  window.location.href = `${window.location.origin}${action.url}`;
}
